import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Calendar, Code, Download, Loader2, MessageSquareText, RefreshCw } from "lucide-react";
import { Typography } from "@/components/ui/atoms/Typography";
import { dataTableSortHeader } from "@/components/ui/molecules/DataTable";
import type { Interview } from "@/services/interview.service";
import { Button } from "@/components/ui/atoms/Button";
import { IconWrapper } from "@/components/ui/atoms/IconWrapper";
import { Tag } from "@/components/ui/atoms/Tag";

export interface CompletedInterviewTableRow {
  id: string;
  companyName: string;
  creatorEmail: string;
  jobTitle: string;
  candidateName: string;
  interviewId: string;
  startedLabel: string;
  completedLabel: string;
  interview: Interview;
}

export function mapInterviewToCompletedRow(interview: Interview): CompletedInterviewTableRow {
  const startedLabel = interview.started_at
    ? format(new Date(interview.started_at * 1000), "PPp")
    : "N/A";
  const completedLabel = interview.completed_at
    ? format(new Date(interview.completed_at), "PPp")
    : "N/A";

  return {
    id: interview.interview_id,
    companyName: interview.company_name || "",
    creatorEmail: interview.creator_email || "",
    jobTitle: interview.job_title || "",
    candidateName: interview.candidate_name || "",
    interviewId: interview.interview_id,
    startedLabel,
    completedLabel,
    interview,
  };
}

export interface CompletedInterviewTableColumnsOptions {
  onOpenFeedback: (interview: Interview) => void;
  onDownload: (interviewId: string) => void;
  onReloadAssessment: (interviewId: string) => void;
  isDownloading: Record<string, boolean>;
  isReloading: Record<string, boolean>;
}

export function createCompletedInterviewTableColumns({
  onOpenFeedback,
  onDownload,
  onReloadAssessment,
  isDownloading,
  isReloading,
}: CompletedInterviewTableColumnsOptions): ColumnDef<CompletedInterviewTableRow>[] {
  return [
    {
      accessorKey: "companyName",
      meta: { label: "Company" },
      header: dataTableSortHeader("Company"),
      size: 200,
      minSize: 140,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <Typography variant="body-sm" weight="semibold" color="primary">
            {row.original.interview.company_name || "N/A"}
          </Typography>
          <Typography variant="body-sm" weight="medium" color="muted">
            {row.original.interview.creator_email || "N/A"}
          </Typography>
        </div>
      ),
    },
    {
      accessorKey: "jobTitle",
      meta: { label: "Job title" },
      header: dataTableSortHeader("Job title"),
      size: 240,
      minSize: 200,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="semibold" color="primary">
          {row.original.jobTitle}
        </Typography>
      ),
    },
    {
      accessorKey: "candidateName",
      meta: { label: "Candidate" },
      header: dataTableSortHeader("Candidate"),
      size: 140,
      minSize: 96,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="semibold" color="primary">
          {row.original.candidateName}
        </Typography>
      ),
    },
    {
      accessorKey: "interviewId",
      meta: { label: "Interview ID" },
      header: dataTableSortHeader("Interview ID"),
      size: 120,
      minSize: 88,
      cell: ({ row }) => (
        <Tag intent="primary" variant="subtle" className="inline-flex items-center gap-1.5">
          <IconWrapper size="xs"><Code /></IconWrapper>
          {row.original.interviewId.substring(0, 6)}
        </Tag>
      ),
    },
    {
      accessorKey: "startedLabel",
      meta: { label: "Started" },
      header: dataTableSortHeader("Started"),
      size: 200,
      minSize: 168,
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <IconWrapper size="xs"><Calendar /></IconWrapper>
          <Typography variant="caption" color="secondary">
            {row.original.startedLabel}
          </Typography>
        </div>
      ),
    },
    {
      accessorKey: "completedLabel",
      meta: { label: "Completed" },
      header: dataTableSortHeader("Completed"),
      size: 200,
      minSize: 168,
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <IconWrapper size="xs"><Calendar /></IconWrapper>
          <Typography variant="caption" color="secondary">
            {row.original.completedLabel}
          </Typography>
        </div>
      ),
    },
    {
      id: "actions",
      meta: { label: "Actions" },
      header: () => (
        <span className="block whitespace-normal break-words text-center font-heading text-xs font-bold uppercase leading-snug tracking-wider text-dark-invirtus">
          Actions
        </span>
      ),
      size: 320,
      minSize: 280,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const id = row.original.id;
        const downloading = !!isDownloading[id];
        const reloading = !!isReloading[id];
        return (
          <div className="flex justify-center gap-1.5">
            <Button type="contained"
              intent="success-soft"
              size="xs"
              label="Feedback"
              onClick={() => onOpenFeedback(row.original.interview)}
              disabled={!row.original.interview.feedback_rating}
              icon={<MessageSquareText />}
            />
            <Button type="contained"
              intent="muted"
              size="xs"
              label="Download"
              onClick={() => onDownload(id)}
              disabled={downloading}
              icon={ downloading ? <Loader2 className="animate-spin"/> : <Download />}
            />
            <Button type="contained"
              intent="danger-soft"
              size="xs"
              label="Regenerate"
              onClick={() => onReloadAssessment(id)}
              disabled={reloading}
              icon={ reloading ? <Loader2 className="animate-spin"/> : <RefreshCw />}
            />
          </div>
        );
      },
    },
  ];
}
